import { useEffect, useState } from 'react';
import { settingsApi } from '../../api/services';
import type { CompanySettingsDto } from '../../api/types';
import { Alert, Button, Card, Field, Form, Loadable, PageHeader, useAction, useAsync } from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { PERM, useCan } from '../../ui/access';

/** Parámetros contables de la empresa: las cuentas que usan los procesos automáticos (cierre anual, diferencial cambiario). */
export function SettingsPage() {
  const settings = useAsync(() => settingsApi.get(), [], true, false);
  return (
    <>
      <PageHeader title="Parámetros contables" subtitle="Cuentas que usan los procesos automáticos de su empresa" />
      <PageHelp page="settings" />
      <Loadable state={settings}>{(s) => <SettingsForm initial={s} onSaved={settings.setData} />}</Loadable>
    </>
  );
}

function SettingsForm({ initial, onSaved }: { initial: CompanySettingsDto; onSaved: (s: CompanySettingsDto) => void }) {
  const canEdit = useCan(PERM.setup);
  const [retained, setRetained] = useState(initial.retainedEarningsAccount ?? '');
  const [gain, setGain] = useState(initial.fxGainAccount ?? '');
  const [loss, setLoss] = useState(initial.fxLossAccount ?? '');
  const [opening, setOpening] = useState(initial.inventoryOpeningAccount ?? '');
  const { run, busy, fieldErrors } = useAction();
  useEffect(() => {
    setRetained(initial.retainedEarningsAccount ?? '');
    setGain(initial.fxGainAccount ?? '');
    setLoss(initial.fxLossAccount ?? '');
    setOpening(initial.inventoryOpeningAccount ?? '');
  }, [initial]);

  if (!initial.available)
    return <Alert kind="warn">Esta función todavía no está habilitada en la base de datos de su empresa. Contacte al administrador del sistema.</Alert>;

  return (
    <Form onSubmit={() => run(async () => {
      const saved = await settingsApi.update({ retainedEarningsAccount: retained || null, fxGainAccount: gain || null, fxLossAccount: loss || null, inventoryOpeningAccount: opening || null });
      onSaved(saved);
    }, { success: 'Parámetros guardados.' })}>
      <Card title="Cierre anual">
        <Field label="Cuenta de resultados acumulados" error={fieldErrors.RetainedEarningsAccount}
          hint="Cuenta de patrimonio (de detalle) que recibe la utilidad o la pérdida de cada año al hacer el cierre anual.">
          <ClearablePicker value={retained} onChange={setRetained} disabled={!canEdit} />
        </Field>
        {initial.retainedEarningsAccountName && <p className="acc-muted">Actual: {initial.retainedEarningsAccount} · {initial.retainedEarningsAccountName}</p>}
      </Card>
      <Card title="Diferencial cambiario">
        <p className="acc-muted">Se usan al revaluar al tipo de cambio del cierre los saldos en moneda extranjera (bancos, clientes, proveedores).</p>
        <div className="acc-grid-form">
          <Field label="Cuenta de ganancia cambiaria" hint="Cuenta de ingresos." error={fieldErrors.FxGainAccount}>
            <ClearablePicker value={gain} onChange={setGain} disabled={!canEdit} />
          </Field>
          <Field label="Cuenta de pérdida cambiaria" hint="Cuenta de gastos." error={fieldErrors.FxLossAccount}>
            <ClearablePicker value={loss} onChange={setLoss} disabled={!canEdit} />
          </Field>
        </div>
      </Card>
      <Card title="Inventario inicial">
        <Field label="Cuenta del inventario inicial" error={fieldErrors.InventoryOpeningAccount}
          hint="Cuenta de patrimonio (de detalle) contra la que se registran las existencias iniciales que envía el módulo de inventario. Pregunte a su contador cuál usar (por ejemplo, utilidades acumuladas o capital).">
          <ClearablePicker value={opening} onChange={setOpening} disabled={!canEdit} />
        </Field>
        {initial.inventoryOpeningAccountName && <p className="acc-muted">Actual: {initial.inventoryOpeningAccount} · {initial.inventoryOpeningAccountName}</p>}
      </Card>
      {canEdit
        ? <div className="acc-actions"><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
        : <Alert kind="info">Su usuario puede consultar estos parámetros pero no cambiarlos.</Alert>}
    </Form>
  );
}

function ClearablePicker({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <div style={{ flex: 1 }}><AccountPicker value={value} onChange={onChange} disabled={disabled} placeholder="Sin definir" /></div>
      {value && !disabled && <Button kind="link" onClick={() => onChange('')} title="Dejar sin cuenta">Quitar</Button>}
    </div>
  );
}
